import React, { useState, useEffect } from 'react';
import { Copy, Gift, Users, TrendingUp, IndianRupee, Link, AlertTriangle } from 'lucide-react';
import Layout from '../components/Layout';
import { getReferralInfo, getMe } from '../utils/authApi';
import Button from '../components/elements/Button';
import Info from '../components/elements/Info';
import ClaimReferralModal from '../components/ClaimReferralModal';
import { useNavigate } from 'react-router-dom';
import { toDateTimeLocal } from '../utils/globalUtils';

const ReferralPage = () => {
    const CLIENT_BASE = process.env.REACT_APP_CLIENT_URL;
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const [referralData, setReferralData] = useState(null);
    const [referralLink, setReferralLink] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [savedUpiId, setSavedUpiId] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Referral Program | Eternal Locker™";
        if (!isLoggedIn) {
            navigate('/');
            return;
        }
        loadReferralData();
        loadUserProfile();
    }, [isLoggedIn, navigate]);

    // Pre-fills the claim dialog from the UPI ID the last claim stored. This is the only
    // thing still reading it: the profile page no longer collects a UPI ID, and the server
    // no longer accepts one there - a claim is the only thing that writes it, and the
    // payout settling is what clears it again. So an empty answer here is the normal state
    // between payouts, not a failure, and the dialog simply asks for it.
    const loadUserProfile = async () => {
        try {
            const userData = await getMe();
            if (userData.success && userData.user && userData.user.upiId) {
                setSavedUpiId(userData.user.upiId);
            }
        } catch (err) {
        }
    };

    const loadReferralData = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getReferralInfo();
            if (data.success) {
                setReferralData(data);
                setReferralLink(`${CLIENT_BASE}?ref=${data.referralCode}`);
            } else {
                setError(data.message || 'Failed to load referral data');
            }
        } catch (err) {
            setError(err.message || 'Failed to load referral data');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (referralLink) {
            const shareMessage = `Secure your family's most important documents and give them peace of mind with Eternal Locker™! Sign up using my referral link and get started today: ${referralLink}`;
            navigator.clipboard.writeText(shareMessage);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleOpenClaimModal = (referral) => {
        setSelectedReferral(referral);
        setShowClaimModal(true);
    };

    const handleCloseClaimModal = () => {
        setShowClaimModal(false);
        setSelectedReferral(null);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-main"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex sm:flex-row flex-col justify-between items-center border-b border-accent-main pb-4">
                <h1 className="sm:text-3xl text-2xl font-extrabold text-accent-main flex items-center">
                    <Gift className="mr-3 w-7 h-7" />
                    Referral Program
                </h1>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3">
                <p className="text-lg text-primary-dark m-0">
                    Earn rewards by referring friends and family to Eternal Locker™!
                </p>
            </div>

            {error && (
                <Info type="error" className="mb-6">
                    {error}
                </Info>
            )}

            {/* Referral Link Section */}
            <div className="bg-primary-light rounded-xl shadow p-6 mb-6 border border-accent-main">
                <h2 className="text-xl font-semibold mb-4 text-primary-dark flex items-center">
                    <Link className="mr-2" size={24} />
                    Your Referral Link
                </h2>
                <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
                    <div className="flex-1 p-4 rounded-lg bg-background-light border border-accent-main">
                        <code className="text-sm font-mono text-green-600 break-all">
                            {referralLink || 'Loading...'}
                        </code>
                    </div>
                    <button
                        onClick={copyToClipboard}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                    >
                        <Copy size={18} />
                        {copied ? 'Copied!' : 'Copy Link'}
                    </button>
                </div>
                <p className="text-sm text-primary-dark">
                    Clicking "Copy Link" copies a ready-to-share message with your referral link included — just paste it to your friends and family to start earning rewards!
                </p>
            </div>

            {/* Rewards Info Section */}
            <div className="bg-primary-light rounded-xl shadow p-6 mb-6 border border-accent-main">
                <h2 className="text-xl font-semibold mb-4 text-primary-dark flex items-center">
                    <TrendingUp className="mr-2" size={24} />
                    How It Works
                </h2>
                <p className="text-primary-dark mb-4">
                    Earn rewards when your referrals subscribe to any paid plan:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-800 rounded-lg p-4 border border-accent-main">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">Silver Plan</span>
                            <span className="text-white/80 text-sm">₹999</span>
                        </div>
                        <div className="flex items-center text-green-600 font-bold text-xl">
                            <IndianRupee size={20} />
                            <span>250</span>
                        </div>
                        <p className="text-xs text-white/80 mt-1">per referral</p>
                    </div>
                    <div className="bg-yellow-800 rounded-lg p-4 border border-accent-main">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">Gold Plan</span>
                            <span className="text-white/80 text-sm">₹1,499</span>
                        </div>
                        <div className="flex items-center text-green-600 font-bold text-xl">
                            <IndianRupee size={20} />
                            <span>500</span>
                        </div>
                        <p className="text-xs text-white/80 mt-1">per referral</p>
                    </div>
                    <div className="bg-purple-800 rounded-lg p-4 border border-accent-main">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">Platinum Plan</span>
                            <span className="text-white/80 text-sm">₹2,499</span>
                        </div>
                        <div className="flex items-center text-green-600 font-bold text-xl">
                            <IndianRupee size={20} />
                            <span>1,000</span>
                        </div>
                        <p className="text-xs text-white/80 mt-1">per referral</p>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            {referralData?.stats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-primary-light rounded-xl shadow p-6 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-3xl font-bold text-blue-600 mb-2">
                                    {referralData.stats.totalReferrals}
                                </div>
                                <div className="text-primary-dark/80 text-sm">Total Referrals</div>
                            </div>
                            <Users className="text-blue-500" size={32} />
                        </div>
                    </div>
                    <div className="bg-primary-light rounded-xl shadow p-6 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-3xl font-bold text-green-600 mb-2">
                                    {referralData.stats.verifiedReferrals}
                                </div>
                                <div className="text-primary-dark/80 text-sm">Verified Subscribers</div>
                            </div>
                            <TrendingUp className="text-green-500" size={32} />
                        </div>
                    </div>
                    <div className="bg-primary-light rounded-xl shadow p-6 border-l-4 border-yellow-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center text-3xl font-bold text-yellow-600 mb-2">
                                    <IndianRupee size={28} />
                                    <span>{referralData.stats.totalRewards}</span>
                                </div>
                                <div className="text-primary-dark/80 text-sm">Total Rewards Earned</div>
                            </div>
                            <Gift className="text-yellow-500" size={32} />
                        </div>
                    </div>
                </div>
            )}

            {/* Referrals List */}
            <div className="bg-primary-light rounded-xl shadow p-6 border border-accent-main">
                <h2 className="text-xl font-semibold mb-4 text-primary-dark">Your Referrals</h2>
                {!referralData?.referrals || referralData.referrals.length === 0 ? (
                    <p className="text-primary-dark/80 text-center py-8">
                        No referrals yet. Start sharing your referral link to earn rewards!
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-background-light rounded-lg border border-accent-main">
                            <thead>
                                <tr className="border-b border-accent-main">
                                    <th className="text-left py-3 px-4 text-primary-dark font-semibold">Name</th>
                                    <th className="text-left py-3 px-4 text-primary-dark font-semibold">Subscription</th>
                                    <th className="text-right py-3 px-4 text-primary-dark font-semibold">Reward</th>
                                    <th className="text-left py-3 px-4 text-primary-dark font-semibold">Joined</th>
                                    <th className="text-center py-3 px-4 text-primary-dark font-semibold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referralData.referrals.map((referral, index) => (
                                    <tr key={index} className="border-b border-accent-main hover:bg-accent-main/10">
                                        <td className="py-3 px-4 text-primary-dark">
                                            {`${referral.firstName || ''} ${referral.lastName || ''}`.trim() || referral.email}
                                        </td>
                                        <td className="py-3 px-4 text-primary-dark text-sm">
                                            {referral.subscriptionType || 'Not Subscribed'}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <span className="flex items-center justify-end text-green-600 font-semibold">
                                                <IndianRupee size={16} />
                                                <span>{referral.referralAmount}</span>
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-primary-dark">
                                            {toDateTimeLocal(referral.joinedAt).split(',')[0]}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            {referral.referralAmount > 0 ? (
                                                <>
                                                    {referral.referralAmountClaimStatus === 'Paid' && (
                                                        <span className="text-green-600">
                                                            Paid
                                                        </span>
                                                    )}
                                                    {referral.referralAmountClaimStatus === 'Processing' && (
                                                        <span className="text-blue-700">
                                                            Processing
                                                        </span>
                                                    )}
                                                    {referral.referralAmountClaimStatus === 'Unpaid' && (
                                                        sessionStorage.getItem('isSubscribed') !== 'true' ? (
                                                            <Button type="warning" size="sm" className="px-2" onClick={() => navigate('/subscription-check')}>
                                                                <AlertTriangle className="mr-1" size={16} />
                                                                Subscribe to Claim
                                                            </Button>
                                                        ) : (
                                                        <Button size="sm" onClick={() => handleOpenClaimModal(referral)}>
                                                            Claim
                                                        </Button>
                                                        )
                                                    )}
                                                    {referral.referralAmountClaimStatus === 'Failed' && (
                                                        <span className="text-red-600">
                                                            Failed
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-primary-dark text-sm">No reward</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Claim Modal */}
            <ClaimReferralModal
                open={showClaimModal}
                onClose={handleCloseClaimModal}
                selectedReferral={selectedReferral}
                savedUpiId={savedUpiId}
                onClaimSuccess={loadReferralData}
            />
        </Layout>
    );
};

export default ReferralPage;

